<template>
  <Modal
    :modelValue="modelValue"
    @close="handleClose"
  >
    <div
      class="carriage-modal"
      ref="contentRef"
    >
      <div class="carriage-modal__header">
        <h1>Delstrekning</h1>
        <h2>{{ departureStation }} - {{ arrivalStation }}</h2>
      </div>

      <div class="carriage-modal__content">
        <CarriageLayout
          ref="layoutRef"
          :serviceName="serviceName"
          :serviceIdentifier="serviceIdentifier"
          :fromStation="fromStation"
          :toStation="toStation"
          :travelDate="travelDate"
          :bookingNumber="bookingNumber"
          :passengerInfo="passengerInfo"
          :selectedSeats="convertedSeats"
          :currentSegment="currentSegmentObject"
          :legIndex="legIndex"
          :seatPrices="seatPrices"
          :carriageLayout="carriageLayout"
          @seatSelected="handleSeatSelected"
        />
      </div>

      <div class="carriage-modal__actions">
        <Button
          class="carriage-modal__button"
          theme="secondary"
          @click="handleClose"
        >
          {{ $t('common.actions.cancel') }}
        </Button>
        <Button
          class="carriage-modal__button"
          @click="handleSave"
        >
          {{ $t('common.actions.saveSelection') }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts">
  import { defineComponent, ref, PropType, computed } from 'vue'
  import Modal from '@/components/BaseComponents/Modal/Modal.vue'
  import CarriageLayout from './CarriageLayout.vue'
  import Button from '@/components/BaseComponents/Button.vue'
  import type { PassengerInfo } from '@/types/passengerTypes'
  import type { CarriageSeat } from '@/types/seatTypes'

  export default defineComponent({
    name: 'CarriageModal',
    components: {
      Modal,
      CarriageLayout,
      Button,
    },
    props: {
      modelValue: {
        type: Boolean,
        required: true,
      },
      departureStation: {
        type: String,
        required: true,
      },
      arrivalStation: {
        type: String,
        required: true,
      },
      serviceName: {
        type: String,
        required: true,
      },
      serviceIdentifier: {
        type: String,
        required: true,
      },
      fromStation: {
        type: String,
        required: true,
      },
      toStation: {
        type: String,
        required: true,
      },
      travelDate: {
        type: String,
        required: true,
      },
      bookingNumber: {
        type: String,
        required: true,
      },
      passengerInfo: {
        type: Array as PropType<PassengerInfo[]>,
        required: true,
      },
      selectedSeats: {
        type: Array as PropType<CarriageSeat[]>,
        required: true,
      },
      currentSegment: {
        type: String as PropType<'outbound' | 'inbound'>,
        required: true,
      },
      legIndex: {
        type: Number,
        required: true,
      },
      seatPrices: {
        type: Object as PropType<{ [key: number]: number }>,
        required: true,
      },
      carriageLayout: {
        type: Object as PropType<any>,
        required: true,
      },
    },
    emits: ['update:modelValue', 'close', 'save', 'seatSelected'],
    setup(props, { emit }) {
      const layoutRef = ref()
      const contentRef = ref()
      const convertedSeats = computed(() => {
        // Ensure we always return an array of CarriageSeat objects
        return Array.isArray(props.selectedSeats) ? props.selectedSeats : []
      })

      const currentSegmentObject = computed(() => ({
        direction: props.currentSegment,
        serviceIdentifier: props.serviceIdentifier,
        fromStation: props.fromStation,
        toStation: props.toStation,
        travelDate: props.travelDate,
        serviceName: props.serviceName,
        segmentId: `${props.currentSegment}_${props.legIndex}`,
      }))

      const handleClose = () => {
        emit('update:modelValue', false)
        emit('close')
      }

      const handleSave = () => {
        emit('save')
        handleClose()
      }

      const handleSeatSelected = (data: {
        seat: CarriageSeat
        legIndex: number
        direction: any
      }) => {
        const direction =
          typeof data.direction === 'object'
            ? data.direction.direction
            : data.direction
        emit('seatSelected', {
          seat: data.seat,
          legIndex: data.legIndex,
          direction: direction,
        })
      }

      return {
        layoutRef,
        contentRef,
        convertedSeats,
        currentSegmentObject,
        handleClose,
        handleSave,
        handleSeatSelected,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .carriage-modal {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 90vh;

    &__header {
      flex-shrink: 0;
      padding: 1rem;
      border-bottom: 1px solid $clr-primary-100;
      background: $clr-white;
      position: sticky;
      top: 0;
      z-index: 10;
      text-align: center;

      h1 {
        @include title-medium;
        color: $clr-primary;
      }
      h2 {
        margin: 0;
        @include body-medium;
        color: $clr-text;
      }
    }

    &__content {
      flex: 1;
      overflow-y: auto;
      padding: 1rem;
      background: $clr-white;
      min-height: 0; /* Important for Firefox */
    }

    &__actions {
      flex-shrink: 0;
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding: 1rem;
      background: white;
      border-top: 1px solid #e5e7eb;
      position: sticky;
      bottom: 0;
      z-index: 10;
    }

    &__button {
      flex: 1;

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }
    }
  }
</style>
